import dayjs from "dayjs";
import "dayjs/locale/es"; // Importa el idioma español

export default class ToolsService {
  /**
   * Obtiene el símbolo de la moneda.
   * @param name - nom de la data del socio
   * @returns la primera letra del nombre
   */
  userInitial(name: string): string {
    if (name && name.length > 0) {
      return name.charAt(0).toUpperCase();
    }
    return "";
  }
  /**
   * Obtiene el símbolo de la moneda.
   * @param coin - Moneda (1 = S/, 2 = $).
   * @returns El símbolo de la moneda.
   */
  getCoin(coin: number): string {
    if (coin === 2) {
      return "$ ";
    }
    if (coin === 1) {
      return "S/ ";
    }
    return "S/ ";
  }

  getFloat(number: number | null | undefined): string {
    if (number === null || number === undefined || isNaN(Number(number))) {
      return "0.00"; // o '', depende de cómo quieras manejar valores no válidos
    }

    const num = Number(number); // asegúrate de que sea numérico

    const isScientificNotation = /\d+\.?\d*[eE][-+]?\d+/.test(num.toString());

    if (isScientificNotation) {
      return parseFloat(num.toString()).toFixed(2);
    }

    const integerPart = Math.floor(num);
    const decimalPart = (num - integerPart).toFixed(2).slice(2);

    const formattedIntegerPart = integerPart
      .toLocaleString("en-US", { useGrouping: true })
      .replace(/,/g, " ");

    return `${formattedIntegerPart}.${decimalPart}`;
  }

  /**
   * Formatea una fecha a "DD/MM/YYYY".
   * @param timestamp - Marca de tiempo (timestamp).
   * @returns Fecha formateada o "---" si es inválida.
   */
  formatDate(timestamp: string | number | Date): string {
    const formatoFecha = "DD/MM/YYYY";
    const date = dayjs(timestamp);
    return date.isValid() ? date.locale("es").format(formatoFecha) : "---";
  }

  /**
   * Formatea una fecha en formato ISO.
   * @param timestamp - Marca de tiempo (timestamp).
   * @returns Fecha formateada o "---" si es inválida.
   */
  formatDateISO(timestamp: string | number | Date): string {
    const formatoFecha = "ddd DD MMM YYYY";
    const date = dayjs(timestamp);
    return date.isValid() ? date.locale("es").format(formatoFecha) : "---";
  }

  /**
   * Formatea una hora en formato "hh:mm A".
   * @param timestamp - Marca de tiempo (timestamp).
   * @returns Hora formateada o "---" si es inválida.
   */
  formatHourISO(timestamp: string | number | Date): string {
    const formatoFecha = "hh:mm A";
    const date = dayjs(timestamp);
    return date.isValid() ? date.locale("es").format(formatoFecha) : "---";
  }

  /**
   * Formatea una fecha y hora en formato detallado.
   * @param timestamp - Marca de tiempo (timestamp).
   * @returns Fecha y hora formateada o "---" si es inválida.
   */
  formatDateHour(timestamp: string | number | Date): string {
    const formatoFecha = "ddd DD MMM YYYY hh:mm a";
    const date = dayjs(timestamp);
    return date.isValid() ? date.locale("es").format(formatoFecha) : "---";
  }

  /**
   * Convierte una hora de formato 24 horas a 12 horas.
   * @param time24 - Hora en formato "hh:mm:ss".
   * @returns Hora en formato "hh:mm AM/PM".
   */
  formatHour(time24: string): string {
    if (!time24 || typeof time24 !== "string") {
      console.error(
        'Formato inválido. La hora debe ser una cadena en formato "hh:mm:ss".'
      );
      return "---";
    }

    const [hour24, minute] = time24.split(":").map(Number);

    if (
      isNaN(hour24) ||
      isNaN(minute) ||
      hour24 < 0 ||
      hour24 > 23 ||
      minute < 0 ||
      minute > 59
    ) {
      console.error('Hora inválida. Debe tener el formato "hh:mm:ss".');
      return "---";
    }

    const hour12 = hour24 % 12 || 12;
    const amPm = hour24 < 12 ? "am" : "pm";

    return `${hour12}:${String(minute).padStart(2, "0")} ${amPm}`;
  }

  /**
   * Capitaliza cada palabra de un texto.
   * @param text - Texto a capitalizar.
   * @returns Texto con cada palabra en mayúscula.
   */
  capitalize(text: string): string {
    if (!text) return "";
    return text
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }

  /**
   * Convierte un texto a minúsculas.
   * @param text - Texto a convertir.
   * @returns Texto en minúsculas.
   */
  lowercase(text: string): string {
    return text ? text.toLowerCase() : "";
  }

  /**
   * Obtiene la inicial de un texto.
   * @param text - Texto a analizar.
   * @returns Inicial del texto.
   */
  inicial(text: string): string {
    return text.length > 0 ? text.charAt(0).toUpperCase() : "";
  }

  /**
   * Oculta palabras dejando visibles los dos primeros caracteres.
   * @param str - Cadena a ocultar.
   * @returns Cadena con palabras ocultas.
   */
  hideWords(str: string): string {
    if (!str || str.trim().length === 0) {
      return "****";
    }
    return str
      .split(" ")
      .map((word, index) => {
        if (index === 0 || word.length > 2) {
          const firstTwoLetters = word.substring(0, 2);
          const hiddenCharacters =
            word.length > 2 ? "*".repeat(word.length - 2) : "";
          return `${firstTwoLetters}${hiddenCharacters}`;
        }
        return word;
      })
      .join(" ");
  }

  /**
   * Oculta caracteres en un email dejando visibles los primeros dos y el dominio.
   * @param email - Dirección de correo electrónico.
   * @returns Email con caracteres ocultos.
   */
  hideEmailCharacters(email: string): string {
    if (!email || email.trim().length === 0) {
      return "****";
    }
    const atIndex = email.indexOf("@");
    if (atIndex >= 2) {
      const prefix = email.substring(0, 2);
      const hiddenCharacters = "*".repeat(atIndex - 2);
      const domain = email.substring(atIndex);
      return `${prefix}${hiddenCharacters}${domain}`;
    }
    return email;
  }

  /**
   * Calcula el porcentaje de desembolso.
   * @param desembolso - Monto total desembolsado.
   * @param saldo - Saldo restante.
   * @returns Porcentaje de desembolso.
   */
  percentDisbursement(desembolso: number, saldo: number): number {
    if (desembolso <= 0) return 0;
    const result = ((desembolso - saldo) / desembolso) * 100;
    return parseFloat(result.toFixed(2));
  }

  /**
   * Convierte el nombre de un mes a su número correspondiente.
   * @param nombreMes - Nombre del mes en español.
   * @returns Número del mes o -1 si no es válido.
   */
  mesANumero(nombreMes: string): number {
    const meses = [
      "enero",
      "febrero",
      "marzo",
      "abril",
      "mayo",
      "junio",
      "julio",
      "agosto",
      "septiembre",
      "octubre",
      "noviembre",
      "diciembre",
    ];

    const indice = meses.indexOf(nombreMes.toLowerCase()) + 1;
    return indice > 0 ? indice : -1;
  }
  calculateImageSize = (
    originalWidth: number,
    originalHeight: number,
    fixedHeight: number
  ) => {
    const aspectRatio = originalWidth / originalHeight;
    const calculatedWidth = fixedHeight * aspectRatio;
    return { width: calculatedWidth, height: fixedHeight };
  };
  convertImageToBase64 = (url: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = url;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx?.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL("image/png");
        resolve(dataURL);
      };
      img.onerror = (error) => {
        reject(error);
      };
    });
  };
  getImageDimensions = (
    imagePath: string
  ): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.onerror = (error) => {
        reject(error);
      };
      img.src = imagePath;
    });
  };
}
